import { HasuraQueries } from "@/api/hasura/queries"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import {
  Cs__Soe_Dashboard_Total_Order_By as SmartMetersOrderBy,
  GetSmartMetersQueryVariables,
} from "@/api/hasura/generated/graphql"
import {
  GetIHDsResponse,
  GetSmartMetersDataFileUrlResponse,
  SmartMetersLastSyncedDateTimeResponse,
} from "@/api/backofficeRest/generated/model"
import { useQuery } from "@/api/utils/useQuery"
import { ISmartMeter } from "@/models/smartMeter/smartMeter.types"
import { SmartMeterParser } from "@/models/smartMeter/smartMeter.parser"
import { SmartMeterColumnParser } from "@/models/operations/smartMeterColumn/smartMeterColumn.parser"
import { ISmartMeterSummary } from "@/models/operations/smartMeterSummary/smartMeterSummary.types"
import { SmartMeterSummaryParser } from "@/models/operations/smartMeterSummary/smartMeterSummary.parser"
import { SmartMeterFilterTypes } from "@/views/operations/smartMeters/opsSmartMeterConfig"
import { nullsFirstInOrder } from "@/api/utils/hasuraHelpers"
import dayjs, { Dayjs } from "dayjs"
import { formatLocalDateTime } from "@/helpers/dateHelper"
import {
  ihdDetailsParser,
  IIhdDetailsRaw,
} from "@/models/ihdDetails/ihdDetails.parser"

export type SmartMeterQueryResult = {
  records: ISmartMeter[]
  totalCount: number
}

export type SmartMeterSummaryQueryResult = {
  records: ISmartMeterSummary[]
  totalCount: number
}

function mapSmartMeterFilters(filters: SmartMeterFilterTypes) {
  const formatListSearch = (value: string[] | undefined) => {
    return {
      _in: value,
    }
  }

  const formatDateSearch = (from?: Dayjs, to?: Dayjs) => {
    return {
      _gte: formatLocalDateTime(from) ?? undefined,
      _lt: formatLocalDateTime(to) ?? undefined,
    }
  }
  const smartMeterFilterArgs: GetSmartMetersQueryVariables = {
    alerts: formatListSearch(filters.alerts),
    alertDate: formatDateSearch(filters.alertDateFrom, filters.alertDateTo),
    alertStatuses: formatListSearch(filters.alertStatuses),
    deviceStatus: formatListSearch(filters.deviceStatuses),
    errorCodes: formatListSearch(filters.errorCodes),
    errorStatuses: formatListSearch(filters.errorStatuses),
    firmwareStatuses: formatListSearch(filters.firmwareStatuses),
    firmwareVersions: formatListSearch(filters.firmwareVersions),
    gbcsVersion: formatListSearch(filters.gbcsVersions),
    installedDate: formatDateSearch(
      filters.installedDateFrom,
      filters.installedDateTo,
    ),
    lastReadDate: {
      ...formatDateSearch(filters.lastReadDateFrom, filters.lastReadDateTo),
      _is_null:
        filters.readingsReceived !== undefined
          ? !filters.readingsReceived
          : undefined,
    },
    lastOrchestrations: formatListSearch(filters.lastOrchestrations),
    manufacturers: formatListSearch(filters.manufacturers),
    meterType: formatListSearch(filters.meterType),
    models: formatListSearch(filters.models),
    orchestrationStatuses: formatListSearch(filters.orchestrationStatuses),
    paymentModes: formatListSearch(filters.paymentModes),
    regions: formatListSearch(filters.regions),
    serviceRequests: formatListSearch(filters.serviceRequests),
    smetsVersion: formatListSearch(filters.smetsVersions),
    ssd: formatDateSearch(filters.ssdFrom, filters.ssdTo),
    variants: formatListSearch(filters.variants),
    workstreams: formatListSearch(filters.workstreams),
  }

  return smartMeterFilterArgs
}

class SmartMeterServiceClass {
  getSmartMeterList(
    search: string,
    page: number,
    pageSize: number,
    orderBy: SmartMetersOrderBy,
    filters: SmartMeterFilterTypes,
  ) {
    if (Object.values(orderBy)[0] === "desc") {
      orderBy = nullsFirstInOrder(orderBy)
    }

    return useQuery({
      ...HasuraQueries.smartMeter.list(
        `%${search}%`,
        (page - 1) * pageSize,
        pageSize,
        orderBy,
        mapSmartMeterFilters(filters),
      ),
      placeholderData: {
        records: [],
        totalCount: 0,
      },
      useRawResponse: true,
      parserFn: (rawData) => {
        return {
          records: rawData.cs__soe_dashboard_total.map((smartMeter) =>
            SmartMeterParser.toDomain(smartMeter),
          ),
          totalCount:
            rawData.cs__soe_dashboard_total_aggregate?.aggregate?.count ?? 0,
        }
      },
    })
  }

  getSmartMetersSummary() {
    return useQuery({
      ...HasuraQueries.smartMeter.summary(),
      useRawResponse: true,
      parserFn: (rawData) => {
        if (!rawData) return undefined
        return SmartMeterSummaryParser.toDomain(rawData)
      },
    })
  }

  getUniqueColumnValues() {
    return useQuery({
      ...HasuraQueries.smartMeter.columnValues(),
      useRawResponse: true,
      parserFn: (rawData) => {
        if (!rawData) return undefined
        return SmartMeterColumnParser.toDomain(rawData)
      },
    })
  }

  getLastSyncedDateTime() {
    return useQuery({
      ...BackofficeRestQueries.smartMeter.syncedDateTime(),
      placeholderData: null,
      useRawResponse: true,
      parserFn: (rawData: SmartMetersLastSyncedDateTimeResponse) =>
        rawData?.lastSyncedDateTime ?? null,
    })
  }

  extractFileUrl(
    data: GetSmartMetersDataFileUrlResponse,
  ): string | null | undefined {
    return data?.fileUrl ?? null
  }

  getSmartMeter(search: string) {
    return useQuery({
      ...HasuraQueries.smartMeter.getSmartMeter(search),
      placeholderData: null,
      useRawResponse: true,
      parserFn: (rawData) => {
        return rawData.cs__soe_dashboard_total.length
          ? SmartMeterParser.toDomain(rawData.cs__soe_dashboard_total[0])
          : null
      },
    })
  }

  getIhdDetails(mpxns: string) {
    return useQuery({
      ...BackofficeRestQueries.smartMeter.getIhdDetails(mpxns),
      placeholderData: [],
      useRawResponse: true,
      // The mpxns are a path segment, so an empty list would request
      // `/in-home-devices/` and 404 before the meterpoints resolve.
      enabled: !!mpxns,
      parserFn: (rawData: GetIHDsResponse) => {
        const ihds = rawData
        if (!ihds?.length) return []
        return ihds
          .map((ihd) => ihdDetailsParser.toDomain(<IIhdDetailsRaw>ihd))
          .sort((a, b) =>
            dayjs(a.dateCommissioned).isAfter(b.dateCommissioned) ? -1 : 1,
          )
      },
    })
  }
}

export const SmartMeterService = new SmartMeterServiceClass()
